import React from 'react';
import { Avatar, Badge, Flex, Text } from '@mantine/core';

export type InviteStatus = null | 'none' | 'pending' | 'expired' | 'accepted' | 'revoked';

const pillByStatus: Record<string, { label: string; color: string }> = {
  pending: { label: 'Invite pending', color: 'orange' },
  expired: { label: 'Invite expired', color: 'red' },
  revoked: { label: 'Invite revoked', color: 'gray' },
};

export const showsInvite = (status?: InviteStatus) => !!status && status in pillByStatus;

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

interface Props {
  name: string;
  status?: InviteStatus;
  isActive?: boolean;
  canSignIn?: boolean;
  colour?: string | null;
}

const namedColours: Record<string, string> = {
  Red: '#FC2E50',
  Orange: '#F26101',
  Yellow: '#FFC940',
  Green: '#51CB68',
  Blue: '#5A85E1',
  Purple: '#735AE1',
  Black: '#4F4F4F',
  Grey: '#828282',
  LightGrey: '#BDBDBD',
};

const avatarColourOf = (colour?: string | null) => {
  if (!colour) return 'scBlue';
  if (colour.startsWith('#')) return colour;
  return namedColours[colour] ?? 'scBlue';
};

const pillFor = ({ status, isActive, canSignIn }: Props) => {
  if (!isActive) return { label: 'Deactivated', color: 'gray' };
  if (showsInvite(status)) return pillByStatus[status as string];
  if (canSignIn) return { label: 'Active', color: 'green' };
  return { label: "Can't sign in", color: 'gray' };
};

function EmployeeInviteHeader(props: Props) {
  const { name, isActive = true, colour } = props;
  const pill = pillFor(props);
  const avatarColour = avatarColourOf(colour);

  return (
    <Flex align="center" gap="sm" mt="md" mb="md" style={{ opacity: isActive ? 1 : 0.5 }}>
      <Avatar color={avatarColour} radius="xl" size={36}>{initialsOf(name)}</Avatar>
      <Text fw={700} size="lg">{name}</Text>
      <Badge color={pill.color} variant="light" size="lg" tt="none">{pill.label}</Badge>
    </Flex>
  );
}

export default EmployeeInviteHeader;
